import { useCallback, useEffect, useRef } from 'react';
import {
    RequestStatus,
    useServerErrorEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { PaymentAbortionReason } from './QuickCheckoutTypes';
import { useNavigate } from 'react-router-dom';
import { useServerFetch } from '../../hooks/ServerStateHooks';
import { Spinner } from 'dg-web-shared/ui/Spinner';

type PaymentState =
    | 'INITIALIZED'
    | 'CREATED'
    | 'AUTHORIZED'
    | 'SETTLED'
    | 'REFUNDED'
    | 'ABORTED';

type PaymentStateResponse = {
    state: PaymentState;
    paymentAbortionReason?: string;
};

export const QuickCheckoutPaymentCallback = ({
    paymentAttemptId,
    ticketId,
    onPaymentSuccessful,
    onPaymentAborted,
    onPaymentRejected,
}: {
    paymentAttemptId: string;
    ticketId: string;
    onPaymentSuccessful: () => void;
    onPaymentAborted: () => void;
    onPaymentRejected: () => void;
}) => {
    const [paymentServerState, fetchPaymentServerState] = useServerFetch<
        PaymentStateResponse,
        { paymentAttemptId: string }
    >(
        ({ paymentAttemptId: id }) => ({
            url: `/ui-api/quickcheckout/payment/${id}/state`,
        }),
        { paymentAttemptId },
    );

    const navigate = useNavigate();
    const gotoTicketPage = useCallback(
        (id: string) => navigate(`/quickcheckout/ticket/${id}`),
        [navigate],
    );

    const paymentAborted = useCallback(() => {
        onPaymentAborted();
        gotoTicketPage(ticketId);
    }, [gotoTicketPage, onPaymentAborted, ticketId]);
    const paymentRejected = useCallback(() => {
        onPaymentRejected();
        gotoTicketPage(ticketId);
    }, [gotoTicketPage, onPaymentRejected, ticketId]);
    const paymentSuccessful = useCallback(() => {
        onPaymentSuccessful();
        gotoTicketPage(ticketId);
    }, [gotoTicketPage, onPaymentSuccessful, ticketId]);

    const timeoutId = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (paymentServerState.status === RequestStatus.SUCCESS) {
            switch (paymentServerState.data?.state) {
                case 'INITIALIZED':
                case 'CREATED':
                    timeoutId.current = window.setTimeout(
                        () => fetchPaymentServerState(),
                        1000,
                    );
                    break;
                case 'SETTLED':
                    paymentSuccessful();
                    break;
                default:
                    if (
                        paymentServerState.data.paymentAbortionReason &&
                        paymentServerState.data.paymentAbortionReason ===
                            PaymentAbortionReason[PaymentAbortionReason.FAILURE]
                    ) {
                        paymentRejected();
                    } else {
                        paymentAborted();
                    }
            }
        }

        return () => {
            if (timeoutId.current !== undefined) {
                window.clearTimeout(timeoutId.current);
            }
        };
    }, [
        fetchPaymentServerState,
        paymentAborted,
        paymentRejected,
        paymentServerState,
        paymentSuccessful,
    ]);

    useServerErrorEffect(paymentServerState, () => {
        paymentAborted();
    });

    return <Spinner />;
};
